import React, { FC, useRef, useState } from "react";

export interface CustomInputProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  name?: string;
  value?: string | number;
  onChange?: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  type?: React.HTMLInputTypeAttribute;
  label?: React.ReactNode;
  error?: string;
  leftSection?: React.ReactNode;
  rightSection?: React.ReactNode;
  children?: React.ReactNode;
  className?: string;
  inputClassName?: string;
  alwaysFocused?: boolean;
  description?: string;
  labelClassName?: string;
  textarea?: boolean;
  rows?: string | number;
  wrapperClassName?: string;
}

const CustomInput: FC<CustomInputProps> = (props) => {
  const {
    name,
    value,
    onChange,
    error,
    type,
    label,
    leftSection,
    rightSection,
    className,
    inputClassName,
    alwaysFocused,
    textarea,
    rows,
    wrapperClassName,
    ...rest
  } = props;

  const [focused, setFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Separate handlers for each element type
  const handleLabelFocus = () => {
    setFocused(true);
    if (textarea) {
      textareaRef.current?.focus();
    } else {
      inputRef.current?.focus();
    }
  };

  const handleInputFocus = () => setFocused(true);
  const handleBlur = () => setFocused(false);

  const focusText = error ? "text-main-red" : "text-dark-blue";
  const focusBg = error ? "bg-main-red/5" : "bg-blue-50/70";
  const focusBorder = error ? "border-main-red" : "border-dark-blue";

  const resolvedRows = typeof rows === "string" ? parseInt(rows, 10) : rows;

  return (
    <div
      className={[
        "relative flex flex-col text-gray-500 w-full",
        className,
        rest.hidden ? "hidden" : "",
      ].join(" ")}
    >
      <label
        onClick={props.children ? undefined : handleLabelFocus}
        tabIndex={0}
        htmlFor={name}
        className={`p-3 px-1 cursor-pointer font-medium ${
          focused || alwaysFocused || value ? `py-2 ${focusText}` : "py-3"
        } ${props.labelClassName}`}
      >
        {label ?? props.name ?? props.placeholder}
      </label>

      {props.description && (
        <p className="text-sm p-1 text-gray-500">{props.description}</p>
      )}

      <div className={`relative ${wrapperClassName}`}>
        <button
          type="button"
          className={`flex items-center duration-150 w-full overflow-hidden ${focusBg} ${
            focused || alwaysFocused || value ? "h-fit" : "h-0"
          }`}
        >
          {leftSection && (
            <div className="absolute left-2 top-1/2 transform -translate-y-1/2">
              {leftSection}
            </div>
          )}

          {props.children ??
            (textarea ? (
              <textarea
                ref={textareaRef}
                value={value}
                onChange={onChange}
                rows={resolvedRows}
                className={[
                  `w-full p-2 pt-4 border-none bg-transparent focus:outline-none`,
                  leftSection ? "pl-10" : "",
                  rightSection ? "pr-10" : "",
                  inputClassName,
                ].join(" ")}
                onFocus={handleInputFocus}
                onBlur={handleBlur}
                {...(rest as React.TextareaHTMLAttributes<HTMLTextAreaElement>)}
              />
            ) : (
              <input
                ref={inputRef}
                type={type ?? "text"}
                value={value}
                onChange={onChange}
                className={[
                  `w-full p-2 pt-4 border-none bg-transparent focus:outline-none`,
                  leftSection ? "pl-10" : "",
                  rightSection ? "pr-10" : "",
                  inputClassName,
                ].join(" ")}
                onFocus={handleInputFocus}
                onBlur={handleBlur}
                {...(rest as React.InputHTMLAttributes<HTMLInputElement>)}
              />
            ))}

          {rightSection && (
            <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
              {rightSection}
            </div>
          )}
        </button>
      </div>

      <hr
        className={`${
          focused || alwaysFocused || value ? focusBorder : "border-gray-300"
        } h-[2px] appearance-none`}
      />

      {error && <span className="text-red-500 text-sm mt-1">{error}</span>}
    </div>
  );
};

export default CustomInput;
